import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LoadingOverlay from "../../components/LoadingOverlay";
import { INTERVIEW_AI_URL } from "../../system/keys";
import {
  CallerNumber,
  Card,
  ConfirmedBox,
  ConfirmedWhen,
  DayButton,
  DayName,
  DayNumber,
  DayRow,
  Notice,
  Page,
  PrimaryButton,
  SectionLabel,
  Subtitle,
  TimeButton,
  TimeGrid,
  Timezone,
  Title,
} from "./styled";

interface Slot {
  slot: string;
  startsAt: string;
  localDate: string;
  localTime: string;
  timezone: string;
  remaining: number;
}

/**
 * Pantalla donde el candidato elige cuándo quiere que le llamemos.
 *
 * Decisiones de diseño, y por qué:
 *
 * - **Día y hora son dos pasos, no una lista.** Hay hasta 72 bloques en tres
 *   días; en una sola lista, elegir se vuelve un trabajo.
 * - **Las horas se muestran en la zona del candidato**, que es la que el
 *   backend ya calculó. Nunca se le pide convertir nada.
 * - **El número que le va a marcar aparece en la confirmación.** Es el dato
 *   que evita que ignore la llamada por venir de un desconocido, y es lo único
 *   que el candidato tiene que recordar de esta pantalla.
 */
const SchedulePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const { t } = useTranslation();

  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [callerNumber, setCallerNumber] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<{ when: string } | null>(null);

  const cargarHorarios = useCallback(async () => {
    if (!id || !token) {
      setError(t("schedule:errors.invalidLink"));
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(
        `${INTERVIEW_AI_URL}/api/v1/scheduled-calls/${id}/slots`,
        { params: { token }, timeout: 20000 },
      );
      setSlots(data?.slots ?? []);
      setCallerNumber(data?.caller_number ?? null);
      setError(null);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      // 403 es el enlace: expirado o de otra persona. Cualquier otra cosa es
      // nuestra, y el candidato no tiene por qué distinguirlas.
      setError(status === 403 ? t("schedule:errors.linkNotValid") : t("schedule:errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [id, token, t]);

  useEffect(() => {
    cargarHorarios();
  }, [cargarHorarios]);

  /** Bloques agrupados por día local, en orden. */
  const dias = useMemo(() => {
    const mapa = new Map<string, Slot[]>();
    slots.forEach((slot) => {
      const actual = mapa.get(slot.localDate) ?? [];
      actual.push(slot);
      mapa.set(slot.localDate, actual);
    });
    return Array.from(mapa.entries());
  }, [slots]);

  useEffect(() => {
    // Se preselecciona el primer día con lugar: abrir la pantalla sin nada
    // elegido obliga a un toque extra que no decide nada.
    if (!selectedDate && dias.length) setSelectedDate(dias[0][0]);
  }, [dias, selectedDate]);

  const horasDelDia = useMemo(
    () => dias.find(([fecha]) => fecha === selectedDate)?.[1] ?? [],
    [dias, selectedDate],
  );

  const formatearDia = (fecha: string) => {
    const [anio, mes, dia] = fecha.split("-").map(Number);
    const objeto = new Date(anio, mes - 1, dia);
    return {
      nombre: objeto.toLocaleDateString("es-MX", { weekday: "short" }),
      numero: objeto.getDate(),
    };
  };

  const agendar = async () => {
    if (!selectedSlot) return;
    setSaving(true);
    try {
      await axios.post(
        `${INTERVIEW_AI_URL}/api/v1/scheduled-calls/${id}/schedule`,
        { slot: selectedSlot.slot, scheduled_by: "candidate", source: "web", token },
        { timeout: 20000 },
      );
      const cuando = new Date(selectedSlot.startsAt).toLocaleString("es-MX", {
        timeZone: selectedSlot.timezone,
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      setConfirmed({ when: cuando.charAt(0).toUpperCase() + cuando.slice(1) });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        // Alguien más tomó el último lugar mientras elegía. Se recargan los
        // horarios para que no vuelva a chocar con el mismo.
        setError(t("schedule:errors.slotTaken"));
        setSelectedSlot(null);
        await cargarHorarios();
      } else {
        setError(status === 403 ? t("schedule:errors.linkNotValid") : t("schedule:errors.saveFailed"));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingOverlay isVisible title={t("schedule:loading.title")} subtitle={t("schedule:loading.subtitle")} />;
  }

  if (confirmed) {
    return (
      <Page>
        <Card>
          <Title>{t("schedule:confirmed.title")}</Title>
          <Subtitle>{t("schedule:confirmed.subtitle")}</Subtitle>
          <ConfirmedBox>
            <ConfirmedWhen>{confirmed.when}</ConfirmedWhen>
            <p style={{ margin: 0, fontSize: 14, color: "#555555" }}>
              {t("schedule:confirmed.callerPrefix")}{" "}
              <CallerNumber>{callerNumber ?? t("schedule:confirmed.callerFallback")}</CallerNumber>
            </p>
          </ConfirmedBox>
          <Notice>{t("schedule:confirmed.tips")}</Notice>
        </Card>
      </Page>
    );
  }

  if (error && !slots.length) {
    return (
      <Page>
        <Card>
          <Title>{t("schedule:errors.title")}</Title>
          <Notice $tone="error">{error}</Notice>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <Card>
        <Title>{t("schedule:title")}</Title>
        <Subtitle>{t("schedule:subtitle")}</Subtitle>

        <SectionLabel>{t("schedule:pickDay")}</SectionLabel>
        <DayRow>
          {dias.map(([fecha]) => {
            const { nombre, numero } = formatearDia(fecha);
            return (
              <DayButton
                key={fecha}
                type="button"
                $selected={fecha === selectedDate}
                onClick={() => {
                  setSelectedDate(fecha);
                  setSelectedSlot(null);
                }}
              >
                <DayName>{nombre}</DayName>
                <DayNumber>{numero}</DayNumber>
              </DayButton>
            );
          })}
        </DayRow>

        <SectionLabel>{t("schedule:pickTime")}</SectionLabel>
        <TimeGrid>
          {horasDelDia.map((slot) => (
            <TimeButton
              key={slot.slot}
              type="button"
              $selected={selectedSlot?.slot === slot.slot}
              onClick={() => setSelectedSlot(slot)}
            >
              {slot.localTime}
            </TimeButton>
          ))}
        </TimeGrid>

        {!horasDelDia.length && <Notice>{t("schedule:noSlotsThisDay")}</Notice>}
        {error && <Notice $tone="error">{error}</Notice>}

        <PrimaryButton type="button" disabled={!selectedSlot || saving} onClick={agendar}>
          {saving ? t("schedule:saving") : t("schedule:confirmButton")}
        </PrimaryButton>

        {slots[0]?.timezone && (
          <Timezone>{t("schedule:timezoneNote", { timezone: slots[0].timezone })}</Timezone>
        )}
      </Card>
    </Page>
  );
};

export default SchedulePage;
